import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { AppLayout } from "@/components/ui-system";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Warehouse, Truck, Users, ArrowLeftRight, Settings,
  FileText, Timer, Receipt, ClipboardList, IndianRupee, Mail, TrendingUp, AlertTriangle,
} from "lucide-react";

const PharmeasyLayout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "admin_user";
  const isAccountManager = user?.role === "account_manager";
  const isRider = user?.role === "pharmeasy_rider";

  const baseNav = [
    { name: "Dashboard", path: "/pharmeasy", icon: LayoutDashboard, testId: "pharmeasy-nav-dashboard", end: true },
    { name: "Orders", path: "/pharmeasy/orders", icon: Package, testId: "pharmeasy-nav-orders" },
  ];
  const hubOps = { name: "Hub Ops", path: "/pharmeasy/hub-ops", icon: Warehouse, testId: "pharmeasy-nav-hub-ops" };
  const trips = { name: "Trips", path: "/pharmeasy/trips", icon: Truck, testId: "pharmeasy-nav-trips" };
  const adminNav = [
    { name: "Riders", path: "/pharmeasy/riders", icon: Users, testId: "pharmeasy-nav-riders" },
    { name: "COD Recon", path: "/pharmeasy/cod-reconciliation", icon: IndianRupee, testId: "pharmeasy-nav-cod-recon" },
    { name: "Escalations", path: "/pharmeasy/escalations", icon: AlertTriangle, testId: "pharmeasy-nav-escalations" },
    { name: "Report", path: "/pharmeasy/report", icon: ClipboardList, testId: "pharmeasy-nav-report" },
    { name: "API Logs", path: "/pharmeasy/api-logs", icon: FileText, testId: "pharmeasy-nav-api-logs" },
    { name: "Notifications", path: "/pharmeasy/notifications", icon: Mail, testId: "pharmeasy-nav-notifications" },
    { name: "Cron Jobs", path: "/pharmeasy/cron-jobs", icon: Timer, testId: "pharmeasy-nav-cron-jobs" },
  ];
  const billingNav = [
    { name: "Billing", path: "/pharmeasy/billing", icon: Receipt, testId: "pharmeasy-nav-billing" },
    { name: "Revenue Projection", path: "/pharmeasy/revenue-projection", icon: TrendingUp, testId: "pharmeasy-nav-revenue" },
  ];
  const accountManagerNav = [baseNav[0], ...billingNav];

  const nav = isAccountManager
    ? accountManagerNav
    : isRider
    ? [...baseNav, trips]
    : isAdmin
    ? [...baseNav, hubOps, trips, ...adminNav, ...billingNav]
    : [...baseNav, hubOps, trips, ...adminNav];

  return (
    <AppLayout
      variant="sidebar"
      accent="green"
      dataBrand="pharmeasy"
      brand={{ name: "Pharmeasy", subtitle: "Operations", logo: "P" }}
      nav={nav}
      user={user}
      onLogout={onLogout}
      profilePath="/pharmeasy/profile"
      topBar={<PharmeasyTopBar isAdmin={isAdmin} isAccountManager={isAccountManager} navigate={navigate} />}
      drawerExtra={
        (isAdmin || isAccountManager) ? (
          <button
            onClick={() => navigate("/admin")}
            className="w-full mb-3 flex items-center gap-3 px-4 py-3 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
            data-testid="mobile-switch-to-main"
          >
            <ArrowLeftRight className="w-5 h-5" />
            <span className="text-sm font-medium">Switch to Main Dashboard</span>
          </button>
        ) : null
      }
    >
      {children}
    </AppLayout>
  );
};

const PharmeasyTopBar = ({ isAdmin, isAccountManager, navigate }) => {
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [config, setConfig] = useState({ assetid: "", assetname: "", authorization: "" });
  const [configMeta, setConfigMeta] = useState({ last_updated_at: null, last_updated_by: null });
  const [saving, setSaving] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/hubs/pharmeasy-config/settings`);
      const { assetid, assetname, authorization, last_updated_at, last_updated_by } = res.data;
      setConfig({ assetid: assetid || "", assetname: assetname || "", authorization: authorization || "" });
      setConfigMeta({ last_updated_at, last_updated_by });
      setConfigLoaded(true);
    } catch { console.error("Config fetch failed"); }
  };
  useEffect(() => { if (isAdmin) fetchConfig(); }, [isAdmin]);

  const handleSave = async () => {
    if (!config.assetid.trim() || !config.assetname.trim() || !config.authorization.trim()) {
      toast.error("All fields are required and cannot be blank"); return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/hubs/pharmeasy-config/settings`, config);
      setConfigMeta({ last_updated_at: res.data.last_updated_at, last_updated_by: res.data.last_updated_by });
      toast.success("Pharmeasy config updated");
      setShowConfigDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update config");
    } finally { setSaving(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return "Never";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <>
      <div className="hidden md:flex items-center justify-between px-8 py-2 bg-white border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">Pharmeasy Ops</span>
          {isAdmin && configLoaded && (
            <button
              onClick={() => setShowConfigDialog(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
              data-testid="pharmeasy-config-btn"
            >
              <Settings className="w-3.5 h-3.5" /> API Config
              {configMeta.last_updated_at && (
                <span className="text-slate-400 ml-1">| Updated {formatDate(configMeta.last_updated_at)}</span>
              )}
            </button>
          )}
        </div>
        {(isAdmin || isAccountManager) && (
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
            data-testid="switch-to-main-dashboard"
          >
            <ArrowLeftRight className="w-4 h-4" /> Switch to Main Dashboard
          </button>
        )}
      </div>

      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pharmeasy API Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="cfg-assetid">Asset ID *</Label>
              <Input id="cfg-assetid" value={config.assetid} onChange={(e) => setConfig({ ...config, assetid: e.target.value })} placeholder="Enter Asset ID" className="mt-1" data-testid="config-assetid-input" />
            </div>
            <div>
              <Label htmlFor="cfg-assetname">Asset Name *</Label>
              <Input id="cfg-assetname" value={config.assetname} onChange={(e) => setConfig({ ...config, assetname: e.target.value })} placeholder="Enter Asset Name" className="mt-1" data-testid="config-assetname-input" />
            </div>
            <div>
              <Label htmlFor="cfg-authorization">Authorization *</Label>
              <Input id="cfg-authorization" value={config.authorization} onChange={(e) => setConfig({ ...config, authorization: e.target.value })} placeholder="Enter Authorization token" className="mt-1" data-testid="config-authorization-input" />
            </div>
            {configMeta.last_updated_at && (
              <div className="text-xs text-slate-400 bg-slate-50 px-3 py-2 rounded-md">
                Last updated: {formatDate(configMeta.last_updated_at)} by {configMeta.last_updated_by || "—"}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="config-save-btn">
              {saving ? "Saving..." : "Save Config"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PharmeasyLayout;
